// QuoteFast engine — itemized service quote builder.

export interface QuoteItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  unit: string;          // "hour", "sqft", "each", "day"
  costPrice?: number;    // for margin guard
}

export interface Quote {
  id: string;
  number: string;
  status: 'draft' | 'sent' | 'accepted' | 'declined' | 'expired';
  date: string;
  validUntil: string;
  business: { name: string; email: string; phone: string };
  customer: { name: string; email: string; phone: string; address: string };
  jobTitle: string;
  items: QuoteItem[];
  discountPercent: number;
  taxPercent: number;
  terms: string;
  notes: string;
  minMarginPercent: number; // for margin guard alert
}

export const ITEM_LIBRARY: Omit<QuoteItem, 'id' | 'quantity'>[] = [
  { description: 'Labor — Standard', unitPrice: 75, unit: 'hour', costPrice: 35 },
  { description: 'Labor — Skilled', unitPrice: 125, unit: 'hour', costPrice: 55 },
  { description: 'Material markup', unitPrice: 0, unit: 'each', costPrice: 0 },
  { description: 'Site visit / consultation', unitPrice: 150, unit: 'each', costPrice: 50 },
  { description: 'Permits & fees (pass-through)', unitPrice: 0, unit: 'each', costPrice: 0 },
  { description: 'Disposal / cleanup', unitPrice: 200, unit: 'each', costPrice: 75 },
];

export function emptyQuote(): Quote {
  const valid = new Date(Date.now() + 30 * 86400_000).toISOString().slice(0, 10);
  const seq = (Date.now() % 100000).toString().padStart(5, '0');
  return {
    id: crypto.randomUUID(),
    number: `Q-${seq}`,
    status: 'draft',
    date: new Date().toISOString().slice(0, 10),
    validUntil: valid,
    business: { name: '', email: '', phone: '' },
    customer: { name: '', email: '', phone: '', address: '' },
    jobTitle: '',
    items: [],
    discountPercent: 0,
    taxPercent: 0,
    terms: 'Quote valid for 30 days. Deposit of 50% required to schedule. Final balance due on completion.',
    notes: '',
    minMarginPercent: 30,
  };
}

export function subtotal(items: QuoteItem[]): number { return items.reduce((s, i) => s + i.quantity * i.unitPrice, 0); }
export function totalCost(items: QuoteItem[]): number { return items.reduce((s, i) => s + i.quantity * (i.costPrice || 0), 0); }
export function discountAmount(q: Quote): number { return subtotal(q.items) * (q.discountPercent / 100); }
export function preTax(q: Quote): number { return subtotal(q.items) - discountAmount(q); }
export function taxAmount(q: Quote): number { return preTax(q) * (q.taxPercent / 100); }
export function grandTotal(q: Quote): number { return preTax(q) + taxAmount(q); }
export function marginPercent(q: Quote): number { const rev = preTax(q); const cost = totalCost(q.items); if (rev <= 0) return 0; return ((rev - cost) / rev) * 100; }
export function isMarginAlert(q: Quote): boolean { return marginPercent(q) < q.minMarginPercent; }

export function formatMoney(n: number, currency = 'USD'): string { try { return new Intl.NumberFormat('en', { style: 'currency', currency }).format(n); } catch { return `$${n.toFixed(2)}`; } }

export function exportQuoteHTML(q: Quote): string {
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${q.number}</title>
<style>body{font-family:-apple-system,sans-serif;color:#0a1628;max-width:800px;margin:40px auto;padding:32px;background:#fff}h1{margin:0 0 8px;color:#0a1628}.muted{color:#64748b;font-size:13px}.row{display:flex;justify-content:space-between;gap:32px;margin:24px 0}.row > div{flex:1}table{width:100%;border-collapse:collapse;margin:24px 0;font-size:14px}th,td{padding:12px;border-bottom:1px solid #e5e7eb;text-align:left}th{background:#f9fafb;font-weight:600}.tot{margin-top:24px;margin-left:auto;width:300px}.tot .line{display:flex;justify-content:space-between;padding:6px 0}.tot .grand{font-weight:700;font-size:18px;border-top:2px solid #0a1628;padding-top:10px;color:#d4af37}.terms{margin-top:32px;padding-top:24px;border-top:1px solid #e5e7eb;color:#475569;font-size:13px}</style></head>
<body><h1>Quote ${q.number}</h1><div class="muted">${q.jobTitle}</div>
<div class="row">
  <div><div class="muted">From</div><strong>${q.business.name}</strong><br/>${q.business.email}<br/>${q.business.phone}</div>
  <div><div class="muted">To</div><strong>${q.customer.name}</strong><br/>${q.customer.address.replace(/\n/g,'<br/>')}<br/>${q.customer.email}<br/>${q.customer.phone}</div>
  <div><div class="muted">Date</div>${q.date}<div class="muted" style="margin-top:8px">Valid until</div>${q.validUntil}</div>
</div>
<table><thead><tr><th>Description</th><th style="text-align:right">Qty</th><th style="text-align:right">Unit</th><th style="text-align:right">Price</th><th style="text-align:right">Total</th></tr></thead>
<tbody>${q.items.map(i => `<tr><td>${i.description}</td><td style="text-align:right">${i.quantity}</td><td style="text-align:right">${i.unit}</td><td style="text-align:right">${formatMoney(i.unitPrice)}</td><td style="text-align:right">${formatMoney(i.quantity * i.unitPrice)}</td></tr>`).join('')}</tbody></table>
<div class="tot">
  <div class="line"><span>Subtotal</span><span>${formatMoney(subtotal(q.items))}</span></div>
  ${q.discountPercent ? `<div class="line"><span>Discount (${q.discountPercent}%)</span><span>-${formatMoney(discountAmount(q))}</span></div>` : ''}
  <div class="line"><span>Tax (${q.taxPercent}%)</span><span>${formatMoney(taxAmount(q))}</span></div>
  <div class="line grand"><span>TOTAL</span><span>${formatMoney(grandTotal(q))}</span></div>
</div>
<div class="terms"><strong>Terms</strong><br/>${q.terms.replace(/\n/g,'<br/>')}${q.notes ? `<br/><br/><strong>Notes</strong><br/>${q.notes.replace(/\n/g,'<br/>')}` : ''}</div>
</body></html>`;
}
